import { Video, Wrench, Mail, ArrowUpRight } from "lucide-react";

const SUBJECT = encodeURIComponent("Training & install materials request — Kitchensafe");
const BODY = encodeURIComponent(
    "Hi Tom,\n\nI'm a licensed installer / engineer working on a Kitchensafe project and would like access to the training and installation materials.\n\nProject details:\n- Company / role:\n- Project location:\n- Hood model & size:\n- Cooking appliance & fuel type:\n- Expected install date:\n\nThanks,"
);

const RESOURCES = [
    {
        icon: Video,
        title: "Free video training & certification",
        body: "Walk through every step of system operation, inspection, and the two-stage heat sensor logic.",
    },
    {
        icon: Wrench,
        title: "Step-by-step install guide",
        body: "Photos, diagrams, and a PowerPoint deck — exactly how a Kitchensafe goes from box to fire-marshal ready.",
    },
];

function ResourceCell({ resource }) {
    const Icon = resource.icon;
    return (
        <div
            data-testid={`installer-resource-${resource.title}`}
            className="bg-[#0a0a0b] hover:bg-[#141416] transition-colors p-6 md:p-8 group"
        >
            <div className="w-10 h-10 flex items-center justify-center border border-white/10 group-hover:border-[#E53935]/50 group-hover:bg-[#E53935]/5 transition-all mb-4">
                <Icon className="w-4 h-4 text-white" strokeWidth={1.75} />
            </div>
            <div className="font-display font-bold text-base md:text-lg text-white mb-1.5 tracking-tight">
                {resource.title}
            </div>
            <p className="text-sm text-[#a1a1aa] leading-relaxed">{resource.body}</p>
        </div>
    );
}

export default function InstallerResources() {
    return (
        <section
            id="installer-resources"
            data-testid="installer-resources-section"
            className="relative py-20 md:py-24 border-t border-white/10 bg-[#0a0a0b]"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935]">
                                / 06 — For Installers & Engineers
                            </span>
                        </div>
                        <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                            The first one is{" "}
                            <span className="text-[#E53935] italic font-medium">always</span> the
                            trickiest.
                        </h2>
                        <p className="mt-8 text-base md:text-lg text-[#a1a1aa] leading-relaxed">
                            Kitchensafe installation is straightforward — but no manual replaces a
                            real person on the phone the first time through. Tom personally walks
                            every first-time installer through the system, helping save time and
                            prevent accidental activation.
                        </p>
                        <p className="mt-5 text-sm md:text-base text-[#a1a1aa] leading-relaxed">
                            Training materials and the install PowerPoint are available on
                            request — they come with a same-day call from Tom.
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="grid gap-px bg-white/5 border border-white/10">
                            {RESOURCES.map((r) => (
                                <ResourceCell key={r.title} resource={r} />
                            ))}
                        </div>

                        <a
                            href={`mailto:tom@kitchensafe.net?subject=${SUBJECT}&body=${BODY}`}
                            data-testid="installer-request-cta"
                            className="mt-px w-full flex items-center justify-between gap-4 p-6 md:p-8 bg-[#E53935] hover:bg-[#F44336] text-white transition-colors group"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 flex items-center justify-center border border-white/20 flex-shrink-0">
                                    <Mail className="w-4 h-4 text-white" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-white/80 mb-1">
                                        Request training materials
                                    </div>
                                    <div className="font-display font-bold text-lg md:text-xl text-white tracking-tight">
                                        Email Tom directly
                                    </div>
                                </div>
                            </div>
                            <ArrowUpRight className="w-5 h-5 text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
